import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail, API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Upload, Link as LinkIcon, EnvelopeSimple, FilePdf, ArrowLeft, PencilSimple, Eye, ImageBroken, MagnifyingGlass } from "@phosphor-icons/react";

// Helper: build a fully-qualified image URL using the API base origin.
const imgUrl = (rel) => {
  if (!rel) return "";
  try {
    return new URL(rel, API_BASE).toString();
  } catch {
    return rel;
  }
};

export default function WalkthroughsPanel() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/walkthroughs");
      setList(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load walkthroughs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="card-flat p-10 text-muted-foreground" data-testid="walkthroughs-loading">Loading walkthroughs…</div>;
  }

  if (selectedId) {
    return (
      <WalkthroughDetail
        wid={selectedId}
        onBack={() => { setSelectedId(null); load(); }}
      />
    );
  }

  const filtered = filter
    ? list.filter((w) =>
        (w.title + " " + w.category + " " + w.feature_route).toLowerCase().includes(filter.toLowerCase())
      )
    : list;

  const grouped = filtered.reduce((acc, w) => {
    (acc[w.category || "General"] ||= []).push(w);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="walkthroughs-panel">
      <div className="card-flat p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Walkthroughs</div>
            <h3 className="font-serif text-2xl">Annotated screenshot slideshows for every feature.</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Build per-feature step-by-step guides. Share via public link, email, or download as PDF.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter…"
                className="input-dark pl-9 pr-3 py-2 text-sm"
                data-testid="walkthroughs-filter"
              />
            </div>
            <DemoOfTheDayEmbedButton />
            <ExportImportButtons onChanged={load} />
            <NewWalkthroughButton onCreated={(w) => { load(); setSelectedId(w.id); }} />
          </div>
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="card-flat p-12 text-center text-muted-foreground" data-testid="walkthroughs-empty">
          No walkthroughs match.
        </div>
      ) : (
        Object.keys(grouped).sort().map((cat) => (
          <section key={cat} className="card-flat p-6" data-testid={`walkthrough-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="eyebrow mb-4">{cat}</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[cat].map((w) => (
                <button
                  key={w.id}
                  onClick={() => setSelectedId(w.id)}
                  className="text-left p-5 border hairline hover:border-[hsl(var(--secondary))] transition group"
                  data-testid={`walkthrough-card-${w.slug}`}
                >
                  <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
                    <code>{w.feature_route}</code>
                    <span>{w.step_count} step{w.step_count === 1 ? "" : "s"}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-serif text-lg group-hover:text-[hsl(var(--secondary))] transition">{w.title}</div>
                    {w.featured && (
                      <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--secondary))] border border-[hsl(var(--secondary))] px-1.5 py-0.5" data-testid={`walkthrough-featured-badge-${w.slug}`}>
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{w.description || "No description yet."}</p>
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function NewWalkthroughButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "General", feature_route: "/app", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/walkthroughs", form);
      toast.success("Walkthrough created");
      setOpen(false);
      setForm({ title: "", category: "General", feature_route: "/app", description: "" });
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2"
        data-testid="walkthrough-new-button"
      >
        <Plus size={14} weight="bold" /> New walkthrough
      </button>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="walkthrough-new-modal">
      <div className="bg-card border hairline max-w-lg w-full p-6">
        <div className="eyebrow mb-3">New walkthrough</div>
        <div className="space-y-3">
          <input className="input-dark w-full px-3 py-2 text-sm" placeholder="Title (e.g. Add a property)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="walkthrough-new-title" />
          <input className="input-dark w-full px-3 py-2 text-sm" placeholder="Category (e.g. Properties)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="walkthrough-new-category" />
          <input className="input-dark w-full px-3 py-2 text-sm" placeholder="Feature route (e.g. /app/properties)" value={form.feature_route} onChange={(e) => setForm({ ...form, feature_route: e.target.value })} data-testid="walkthrough-new-route" />
          <textarea rows={3} className="input-dark w-full px-3 py-2 text-sm" placeholder="One-line description shown in the catalog…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="walkthrough-new-desc" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setOpen(false)} className="text-xs px-3 py-2 border hairline" data-testid="walkthrough-new-cancel">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-primary px-4 py-2 text-sm" data-testid="walkthrough-new-submit">{submitting ? "Creating…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function WalkthroughDetail({ wid, onBack }) {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/walkthroughs");
      setW(data.find((x) => x.id === wid) || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [wid]);

  useEffect(() => { load(); }, [load]);

  if (loading || !w) return <div className="card-flat p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="walkthrough-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid="walkthrough-detail-back">
          <ArrowLeft size={12} /> Back to catalog
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <ShareButton wid={w.id} />
          {w.featured && <EmbedCodeButton slug={w.slug} />}
          <EmailButton wid={w.id} title={w.title} />
          <DownloadPdfButton wid={w.id} slug={w.slug} disabled={w.steps.length === 0} />
          <DeleteWalkthroughButton wid={w.id} title={w.title} onDeleted={onBack} />
        </div>
      </div>

      <WalkthroughMetaForm w={w} onSaved={load} />

      <StepsEditor w={w} onChange={load} />
    </div>
  );
}

function WalkthroughMetaForm({ w, onSaved }) {
  const [form, setForm] = useState({
    title: w.title, description: w.description, category: w.category, feature_route: w.feature_route, slug: w.slug, featured: !!w.featured,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/walkthroughs/${w.id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-flat p-6" data-testid="walkthrough-meta-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Title</label>
          <input className="input-dark w-full px-3 py-2 text-sm mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="walkthrough-meta-title" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Category</label>
          <input className="input-dark w-full px-3 py-2 text-sm mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="walkthrough-meta-category" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Feature route</label>
          <input className="input-dark w-full px-3 py-2 text-sm mt-1" value={form.feature_route} onChange={(e) => setForm({ ...form, feature_route: e.target.value })} data-testid="walkthrough-meta-route" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Slug</label>
          <input className="input-dark w-full px-3 py-2 text-sm mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="walkthrough-meta-slug" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</label>
          <textarea rows={2} className="input-dark w-full px-3 py-2 text-sm mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="walkthrough-meta-desc" />
        </div>
        <div className="md:col-span-2">
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="walkthrough-meta-featured-label">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              className="h-4 w-4 accent-[hsl(var(--secondary))]"
              data-testid="walkthrough-meta-featured"
            />
            <span>
              Featured on public landing page
              <span className="text-muted-foreground text-xs ml-2">— shown in the "See it in action" section to logged-out visitors</span>
            </span>
          </label>
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={save} disabled={saving} className="btn-primary px-4 py-2 text-sm" data-testid="walkthrough-meta-save">
          {saving ? "Saving…" : "Save details"}
        </button>
      </div>
    </div>
  );
}

function StepsEditor({ w, onChange }) {
  return (
    <div className="card-flat p-6" data-testid="walkthrough-steps-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">Steps ({w.steps.length})</div>
      </div>

      <div className="space-y-4">
        {w.steps.map((s, i) => (
          <StepRow key={s.id} wid={w.id} step={s} index={i} onChange={onChange} />
        ))}
        {w.steps.length === 0 && (
          <div className="text-sm text-muted-foreground" data-testid="walkthrough-steps-empty">No steps yet — upload your first screenshot below.</div>
        )}
      </div>

      <div className="border-t hairline pt-5 mt-5">
        <AddStepRow wid={w.id} nextOrder={Math.max(0, ...w.steps.map((s) => s.order)) + 1} onAdded={onChange} />
      </div>
    </div>
  );
}

function StepRow({ wid, step, index, onChange }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(step.caption);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/walkthroughs/${wid}/steps/${step.id}`, { caption, order: step.order });
      toast.success("Step updated");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this step? The screenshot will also be removed.")) return;
    try {
      await api.delete(`/admin/walkthroughs/${wid}/steps/${step.id}`);
      toast.success("Step deleted");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="border hairline p-4 flex flex-col md:flex-row gap-4" data-testid={`walkthrough-step-${index + 1}`}>
      <div className="md:w-64 shrink-0">
        {step.image_url ? (
          <img src={imgUrl(step.image_url)} alt={step.caption} className="w-full border hairline" data-testid={`walkthrough-step-image-${index + 1}`} />
        ) : (
          <div className="w-full aspect-video border hairline flex items-center justify-center text-muted-foreground">
            <ImageBroken size={24} />
          </div>
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))]">Step {index + 1}</span>
          <div className="flex gap-1">
            <button onClick={() => setEditing(!editing)} className="text-xs px-2 py-1 border hairline" title="Edit caption" data-testid={`walkthrough-step-edit-${index + 1}`}>
              <PencilSimple size={12} />
            </button>
            <button onClick={remove} className="text-xs px-2 py-1 border hairline text-destructive" title="Delete step" data-testid={`walkthrough-step-delete-${index + 1}`}>
              <Trash size={12} />
            </button>
          </div>
        </div>
        {editing ? (
          <div className="space-y-2">
            <textarea rows={3} className="input-dark w-full px-3 py-2 text-sm" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid={`walkthrough-step-caption-input-${index + 1}`} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setCaption(step.caption); setEditing(false); }} className="text-xs px-3 py-1 border hairline">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-xs px-3 py-1">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        ) : (
          <p className="text-sm leading-relaxed">{step.caption}</p>
        )}
      </div>
    </div>
  );
}

function AddStepRow({ wid, nextOrder, onAdded }) {
  const fileRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!file) { toast.error("Pick a screenshot image"); return; }
    if (!caption.trim()) { toast.error("Caption is required"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("caption", caption);
      fd.append("order", String(nextOrder));
      await api.post(`/admin/walkthroughs/${wid}/steps`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Step added");
      setCaption(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onAdded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="walkthrough-add-step">
      <div className="eyebrow text-[10px]">Add step {nextOrder}</div>
      <div className="flex flex-col md:flex-row gap-3 items-stretch">
        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="walkthrough-add-step-file" />
        <textarea rows={2} className="input-dark flex-1 px-3 py-2 text-sm" placeholder="Caption — what does this step show?" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="walkthrough-add-step-caption" />
        <button onClick={submit} disabled={submitting} className="btn-primary px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="walkthrough-add-step-submit">
          <Upload size={14} weight="bold" /> {submitting ? "Uploading…" : "Add"}
        </button>
      </div>
    </div>
  );
}

function ShareButton({ wid }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/walkthroughs/${wid}/share`);
      const url = `${window.location.origin}${data.url}`;
      try { await navigator.clipboard?.writeText(url); } catch {/* ignore */}
      toast.success("Share link copied", { description: url, duration: 8000 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={onClick} disabled={busy} className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2" title="Generate public share link" data-testid="walkthrough-share-button">
      <LinkIcon size={12} /> {busy ? "…" : "Share link"}
    </button>
  );
}

function EmailButton({ wid, title }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to: "", recipient_name: "", note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.to.trim()) { toast.error("Recipient email is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/walkthroughs/${wid}/email`, form);
      toast.success("Walkthrough emailed", { description: data.share_url, duration: 8000 });
      setOpen(false);
      setForm({ to: "", recipient_name: "", note: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2" title="Email this walkthrough" data-testid="walkthrough-email-button">
        <EnvelopeSimple size={12} /> Email
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="walkthrough-email-modal">
          <div className="bg-card border hairline max-w-md w-full p-6">
            <div className="eyebrow mb-1">Email walkthrough</div>
            <h4 className="font-serif text-lg mb-4">{title}</h4>
            <div className="space-y-3">
              <input className="input-dark w-full px-3 py-2 text-sm" placeholder="Recipient email *" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="walkthrough-email-to" />
              <input className="input-dark w-full px-3 py-2 text-sm" placeholder="Recipient name (optional)" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="walkthrough-email-name" />
              <textarea rows={3} className="input-dark w-full px-3 py-2 text-sm" placeholder="Personal note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="walkthrough-email-note" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="text-xs px-3 py-2 border hairline">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-primary px-4 py-2 text-sm" data-testid="walkthrough-email-send">{busy ? "Sending…" : "Send"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DownloadPdfButton({ wid, slug, disabled }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/walkthroughs/${wid}/download.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `estima-walkthrough-${slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PDF export failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={onClick} disabled={busy || disabled} className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2 disabled:opacity-50" title={disabled ? "Add steps first" : "Download as PDF"} data-testid="walkthrough-pdf-button">
      <FilePdf size={12} /> {busy ? "…" : "PDF"}
    </button>
  );
}

function DeleteWalkthroughButton({ wid, title, onDeleted }) {
  const onClick = async () => {
    if (!window.confirm(`Permanently delete "${title}" and all its screenshots?`)) return;
    try {
      await api.delete(`/admin/walkthroughs/${wid}`);
      toast.success("Walkthrough deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <button onClick={onClick} className="text-xs px-3 py-2 border border-destructive text-destructive inline-flex items-center gap-2" title="Delete walkthrough" data-testid="walkthrough-delete-button">
      <Trash size={12} /> Delete
    </button>
  );
}

// Re-export so Admin.jsx can wire it directly.
export { WalkthroughsPanel };

// ---------- Export / Import bundle helpers ----------

const MAX_BUNDLE_BYTES = 50 * 1024 * 1024; // 50 MB

function downloadJsonBlob(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

function summariseBundle(data) {
  const walkthroughs = data.walkthroughs || [];
  const totalSteps = walkthroughs.reduce((n, w) => n + (w.steps || []).length, 0);
  return { walkthroughs: walkthroughs.length, steps: totalSteps };
}

async function downloadWalkthroughBundle() {
  const { data } = await api.get("/admin/walkthrough-bundle/export");
  const filename = `estima-walkthroughs-${new Date().toISOString().slice(0, 10)}.json`;
  downloadJsonBlob(data, filename);
  return summariseBundle(data);
}

async function uploadWalkthroughBundle(file) {
  const text = await file.text();
  const json = JSON.parse(text);
  const { data } = await api.post("/admin/walkthrough-bundle/import", json);
  return data;
}

function ExportImportButtons({ onChanged }) {
  const [busyExport, setBusyExport] = useState(false);
  const [busyImport, setBusyImport] = useState(false);
  const fileRef = useRef(null);

  const onExport = async () => {
    setBusyExport(true);
    try {
      const summary = await downloadWalkthroughBundle();
      toast.success(`Exported ${summary.walkthroughs} walkthroughs · ${summary.steps} screenshots`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    } finally {
      setBusyExport(false);
    }
  };

  const onImport = async (file) => {
    const reset = () => { if (fileRef.current) fileRef.current.value = ""; };
    if (!file) return;
    if (file.size > MAX_BUNDLE_BYTES) {
      toast.error("File too large (>50 MB).");
      reset();
      return;
    }
    const ok = window.confirm(`Import "${file.name}"? Existing walkthroughs with matching slugs will have their step images REPLACED.`);
    if (!ok) { reset(); return; }
    setBusyImport(true);
    try {
      const data = await uploadWalkthroughBundle(file);
      const errCount = (data.errors || []).length;
      toast.success(
        `Imported ${data.imported_walkthroughs} walkthroughs · ${data.imported_steps} screenshots`,
        { description: errCount ? `${errCount} error(s) — see browser console.` : undefined }
      );
      if (errCount && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Import errors:", data.errors);
      }
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Import failed");
    } finally {
      setBusyImport(false);
      reset();
    }
  };

  return (
    <>
      <button
        onClick={onExport}
        disabled={busyExport}
        className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2"
        title="Download all walkthroughs + screenshots as a single JSON file"
        data-testid="walkthroughs-export-bundle"
      >
        {busyExport ? "…" : "⬇ Export bundle"}
      </button>
      <button
        onClick={() => fileRef.current?.click()}
        disabled={busyImport}
        className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2"
        title="Restore walkthroughs + screenshots from a JSON bundle (e.g. from preview)"
        data-testid="walkthroughs-import-bundle"
      >
        {busyImport ? "Importing…" : "⬆ Import bundle"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        onChange={(e) => onImport(e.target.files?.[0] || null)}
        className="hidden"
        data-testid="walkthroughs-import-bundle-file"
      />
    </>
  );
}

function EmbedCodeButton({ slug }) {
  const [open, setOpen] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://estima.co.in";
  const url = `${origin}/embed/walkthrough/${slug}`;
  const html = `<iframe src="${url}" width="480" height="540" frameborder="0" style="border:1px solid #ddd;" loading="lazy" title="Estima walkthrough"></iframe>`;
  const md = `[![Estima walkthrough](${origin}/icon-192.png)](${origin}/walkthrough/share/${slug})`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard?.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2" title="Get embed code (iframe / markdown)" data-testid="walkthrough-embed-code-button">
        <LinkIcon size={12} /> Embed
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="walkthrough-embed-modal">
          <div className="bg-card border hairline max-w-xl w-full p-6">
            <div className="eyebrow mb-1">Embed this walkthrough</div>
            <p className="text-xs text-muted-foreground mb-4">
              Paste the iframe into any blog, doc or LinkedIn article. The widget rotates to the latest screenshots automatically — no need to re-embed.
            </p>

            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">HTML iframe</label>
            <textarea readOnly rows={3} value={html} className="input-dark w-full px-3 py-2 text-xs font-mono mt-1" data-testid="walkthrough-embed-iframe" onFocus={(e) => e.target.select()} />
            <div className="flex justify-end mt-2">
              <button onClick={() => copy(html, "iframe code")} className="btn-primary text-xs px-3 py-1.5" data-testid="walkthrough-embed-copy-iframe">Copy iframe</button>
            </div>

            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mt-4 block">Direct embed URL</label>
            <input readOnly value={url} className="input-dark w-full px-3 py-2 text-xs font-mono mt-1" onFocus={(e) => e.target.select()} data-testid="walkthrough-embed-url" />
            <div className="flex justify-end mt-2">
              <button onClick={() => copy(url, "URL")} className="text-xs px-3 py-1.5 border hairline">Copy URL</button>
            </div>

            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mt-4 block">Markdown share image</label>
            <input readOnly value={md} className="input-dark w-full px-3 py-2 text-xs font-mono mt-1" onFocus={(e) => e.target.select()} data-testid="walkthrough-embed-markdown" />
            <div className="flex justify-end mt-2">
              <button onClick={() => copy(md, "Markdown")} className="text-xs px-3 py-1.5 border hairline">Copy Markdown</button>
            </div>

            <div className="flex justify-end mt-5">
              <button onClick={() => setOpen(false)} className="text-xs px-3 py-2 border hairline" data-testid="walkthrough-embed-close">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DemoOfTheDayEmbedButton() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://estima.co.in";
  const url = `${origin}/embed/demo-of-the-day`;
  const html = `<iframe src="${url}" width="480" height="540" frameborder="0" style="border:1px solid #ddd;" loading="lazy" title="Estima demo of the day"></iframe>`;

  useEffect(() => {
    if (!open) return;
    api.get("/public/walkthroughs/demo-of-the-day")
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null));
  }, [open]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard?.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs px-3 py-2 border hairline inline-flex items-center gap-2" title="Embeddable widget that rotates daily" data-testid="walkthrough-demo-of-day-button">
        <Eye size={12} /> Demo-of-day embed
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="walkthrough-demo-of-day-modal">
          <div className="bg-card border hairline max-w-xl w-full p-6">
            <div className="eyebrow mb-1">Featured demo of the day</div>
            <p className="text-xs text-muted-foreground mb-4">
              Auto-rotates every UTC midnight across your featured walkthroughs. Embed once and forget.
            </p>
            {preview && (
              <div className="mb-4 p-3 border hairline">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--secondary))] mb-1">Today (rotation {preview.rotation_day} / pool {preview.pool_size})</div>
                <div className="font-serif text-sm" data-testid="walkthrough-demo-of-day-preview-title">{preview.title}</div>
                <div className="text-[11px] text-muted-foreground">{preview.category} · {preview.step_count} steps</div>
              </div>
            )}

            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">HTML iframe</label>
            <textarea readOnly rows={3} value={html} className="input-dark w-full px-3 py-2 text-xs font-mono mt-1" data-testid="walkthrough-demo-of-day-iframe" onFocus={(e) => e.target.select()} />
            <div className="flex justify-end mt-2">
              <button onClick={() => copy(html, "iframe code")} className="btn-primary text-xs px-3 py-1.5" data-testid="walkthrough-demo-of-day-copy-iframe">Copy iframe</button>
            </div>

            <label className="text-[10px] uppercase tracking-wider text-muted-foreground mt-4 block">Direct URL</label>
            <input readOnly value={url} className="input-dark w-full px-3 py-2 text-xs font-mono mt-1" onFocus={(e) => e.target.select()} data-testid="walkthrough-demo-of-day-url" />
            <div className="flex justify-end mt-2">
              <button onClick={() => copy(url, "URL")} className="text-xs px-3 py-1.5 border hairline">Copy URL</button>
            </div>

            <div className="flex justify-end mt-5">
              <button onClick={() => setOpen(false)} className="text-xs px-3 py-2 border hairline" data-testid="walkthrough-demo-of-day-close">Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
